import { useState, useEffect } from 'react';
import { AlertCircle, RefreshCw } from 'react-feather';
import { useAuth } from '../context/AuthContext';
import { ordersAPI } from '../api';
import AdsDisplay from '../components/AdsDisplay';
import { formatCurrencyWithConversion } from '../utils/currency';
import './Orders.css';

const STATUS_COLORS = {
  pending: 'badge-pending', in_progress: 'badge-in_progress',
  completed: 'badge-completed', partial: 'badge-partial', canceled: 'badge-canceled'
};

export default function Orders() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchOrders = async (p = 1) => {
    setLoading(true);
    try {
      // Get recent orders for display
      const res = await ordersAPI.getRecent();
      console.log('Orders API Response:', res.data);
      setOrders(res.data.orders || []);
      setTotal(res.data.total);
      setPage(1);
      setPages(1);
    } catch (err) {
      console.error('Fetch orders error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrders(1); }, []);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <div>
          <h1 className="page-title">My Orders</h1>
          <p className="page-subtitle" style={{ marginBottom: 0 }}>{total} total orders</p>
        </div>
        <button className="btn btn-secondary btn-sm orders-refresh-btn" onClick={() => fetchOrders(page)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <RefreshCw size={16} /> Refresh
        </button>
      </div>

      <AdsDisplay adSlot="banner-300x250" className="medium-rectangle" />

      <AdsDisplay adSlot="banner-320x50" className="banner" />

      <div className="card">
        {loading ? (
          <div style={{ textAlign: 'center', padding: '48px 0' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
        ) : orders && orders.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text-muted)' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}><AlertCircle size={48} style={{ color: 'var(--text-muted)' }} /></div>
            <p style={{ fontSize: 16 }}>No orders yet</p>
          </div>
        ) : orders && orders.length > 0 ? (
          <>
            <div className="table-wrap orders-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Service</th>
                    <th>Link</th>
                    <th>Qty</th>
                    <th>Cost</th>
                    <th>Start</th>
                    <th>Remains</th>
                    <th>Status</th>
                    <th>Refund</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map(o => (
                    <tr key={o.id}>
                      <td style={{ fontFamily: 'monospace', color: 'var(--text-muted)' }}>{o.id}</td>
                      <td>
                        <div style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500 }}>
                          {o.service_name}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{o.category}</div>
                      </td>
                      <td>
                        <a href={o.link} target="_blank" rel="noreferrer"
                          style={{ color: 'var(--accent)', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', fontSize: 13 }}>
                          {o.link}
                        </a>
                      </td>
                      <td>{parseInt(o.quantity).toLocaleString()}</td>
                      <td style={{ color: 'var(--orange)', fontWeight: 600 }}>{formatCurrencyWithConversion(o.cost, user?.currency || 'USD', 4)}</td>
                      <td style={{ color: 'var(--text-muted)' }}>{o.start_count || '—'}</td>
                      <td style={{ color: 'var(--text-muted)' }}>{o.remains || '—'}</td>
                      <td><span className={`badge ${STATUS_COLORS[o.status] || 'badge-pending'}`}>{o.status.replace('_', ' ')}</span></td>
                      <td>
                        {o.refund_status === 'refunded' || o.refund_status === 'manual_refunded' ? (
                          <span style={{ color: 'var(--green-500)', fontSize: 13, fontWeight: 600 }}>
                            +{formatCurrencyWithConversion(o.refund_amount, user?.currency || 'USD', 4)}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--text-dim)', fontSize: 12 }}>—</span>
                        )}
                      </td>
                      <td style={{ color: 'var(--text-muted)', fontSize: 13, whiteSpace: 'nowrap' }}>
                        {new Date(o.created_at).toLocaleDateString()}<br />
                        <span style={{ fontSize: 11 }}>{new Date(o.created_at).toLocaleTimeString()}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pages > 1 && (
              <div className="pagination">
                {Array.from({ length: pages }, (_, i) => i + 1).map(p => (
                  <button key={p} className={`page-btn ${p === page ? 'active' : ''}`} onClick={() => fetchOrders(p)}>{p}</button>
                ))}
              </div>
            )}
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text-muted)' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}><AlertCircle size={48} style={{ color: 'var(--text-muted)' }} /></div>
            <p style={{ fontSize: 16 }}>Error loading orders</p>
          </div>
        )}
      </div>
    </div>
  );
}
